import { Just, type Maybe, Nothing } from "purify-ts";
import { match } from "ts-pattern";
import { areArraysEqualIgnoreOrder } from "widgets/utils";

import type {
  ActivityInstanceParameter,
  FileParam,
  GenomeParam,
  ParameterValue,
  StringControlledValue,
  StringParam,
  TableParam,
  TrackerParam,
} from "@/graphql.ts";
import type {
  Activity,
  ActivityParameter,
  BooleanParamType,
  DateParamType,
  FileParamType,
  NumberParamType,
  OrganismParamType,
  RegionParamType,
  SampleParamType,
  StringParamType,
  TableParamType,
  TrackerParamType,
  WorkflowActivityParameterFieldProps,
  WorkflowParameter,
} from "@/modules/workflow/utils/types.ts";
import { generateValidationSchema } from "@/modules/workflow/utils/validationSchema.ts";

export function createWorkflowParameter<T>(
  nodeId: string,
  parameterId: string,
  activityParameterFromDefinition: ActivityParameter,
  values: Maybe<T>,
  existingStaged?: boolean,
): WorkflowParameter {
  return {
    nodeId,
    parameterId,
    typeName: activityParameterFromDefinition.__typename,
    tracker: (activityParameterFromDefinition as TrackerParam).tracker ?? null,
    values: values == undefined ? Nothing : values,
    staged: existingStaged ?? false,
    multiSelect: (activityParameterFromDefinition as ActivityParameter & { multiSelect: boolean }).multiSelect ?? false,
    schema: generateValidationSchema({
      nodeId,
      ...activityParameterFromDefinition,
    } as WorkflowActivityParameterFieldProps),
  } as WorkflowParameter;
}

export function workflowParamToActivityInstanceParam(workflowParam: WorkflowParameter): ActivityInstanceParameter {
  return {
    id: workflowParam.parameterId,
    disabled: false,
    staged: workflowParam.staged,
    values: convertWorkflowValuesToParameterValue(workflowParam).extractNullable(),
  };
}

/**
 * Converts WorkflowParameter values to GraphQL ParameterValue fragments
 * This is the inverse of getValueFromWorkflowParameter
 */
export function convertWorkflowValuesToParameterValue(param: WorkflowParameter): Maybe<ParameterValue> {
  return match(param)
    .with({ typeName: "StringParam" }, () => {
      const stringValue = (param.values as Maybe<StringParamType>).extractNullable();
      return stringValue
        ? Just({
            id: param.parameterId,
            stringValue,
          })
        : Nothing;
    })
    .with({ typeName: "NumberParam" }, () => {
      const numberValue = (param.values as Maybe<NumberParamType>).extractNullable();
      return numberValue
        ? Just({
            id: param.parameterId,
            numberValue,
          })
        : Nothing;
    })
    .with({ typeName: "BooleanParam" }, () => {
      const booleanValue = (param.values as Maybe<BooleanParamType>).extractNullable();
      return booleanValue
        ? Just({
            id: param.parameterId,
            booleanValue,
          })
        : Nothing;
    })
    .with({ typeName: "GenomeParam" }, () => {
      const genomeIds = (param.values as Maybe<OrganismParamType>).extractNullable();
      return genomeIds
        ? Just({
            id: param.parameterId,
            genomeValue: genomeIds.map((id) => ({
              id: id >= 0 ? id : undefined,
              hidden: id === -1,
            })),
          })
        : Nothing;
    })
    .with({ typeName: "FileParam" }, () => {
      const fileIds = (param.values as Maybe<FileParamType>).extractNullable();
      return fileIds
        ? Just({
            id: param.parameterId,
            fileValue: fileIds.map((id) => ({
              id: id >= 0 ? id : undefined,
              hidden: id === -1,
              deleted: id === -2,
            })),
          })
        : Nothing;
    })
    .with({ typeName: "SampleParam" }, () => {
      const sampleValue = (param.values as Maybe<SampleParamType>).extractNullable();
      return sampleValue
        ? Just({
            id: param.parameterId,
            sampleValue: sampleValue.sampleFileMappings?.map((mapping) => ({
              sample: {
                id: mapping.sampleId >= 0 ? mapping.sampleId : undefined,
                hidden: mapping.sampleId === -1,
                deleted: mapping.sampleId === -2,
              },
            })),
            attribute: {
              id: sampleValue.attributeId,
            },
          })
        : Nothing;
    })
    .with({ typeName: "RegionParam" }, () => {
      const regionValue = (param.values as Maybe<RegionParamType>).extractNullable();
      return regionValue
        ? Just({
            id: param.parameterId,
            regionValue: {
              contig: { id: regionValue.contigId },
              range: regionValue.range,
            },
          })
        : Nothing;
    })
    .with({ typeName: "DateParam" }, () => {
      const dateValue = (param.values as Maybe<DateParamType>).extractNullable();
      return dateValue
        ? Just({
            id: param.parameterId,
            dateValue,
          })
        : Nothing;
    })
    .with({ typeName: "TrackerParam" }, () => {
      const trackerId = (param.values as Maybe<TrackerParamType>).extractNullable();
      return trackerId != null
        ? Just({
            id: param.parameterId,
            trackerValue: { id: trackerId },
          })
        : Nothing;
    })
    .with({ typeName: "TableParam" }, () => {
      const tableId = (param.values as Maybe<TableParamType>).extractNullable();
      return tableId != null
        ? Just({
            id: param.parameterId,
            tableValue: { id: tableId },
          })
        : Nothing;
    })
    .with({ values: Nothing }, () => Nothing)
    .exhaustive();
}

/**
 * Validates if a parameter value from the previous activity version can be retained in the new activity version.
 * A parameter is considered incompatible if<br>

    1) The parameter type does not match anymore
    2) The parameter switched between single/multi value
    3) The tableType does not match for TableParam
    4) A StringParam changed from free text to controlled values (Select instead of TextField)
    5) The file constraints on FileParam changed
 */
export function parametersCompatible(
  existingParam: WorkflowParameter,
  newParam: WorkflowParameter,
  oldActivity: Activity,
  newActivity: Activity,
) {
  const oldActivityDefinition: ActivityParameter | undefined = oldActivity.parameters?.find(
    (p) => p.id === existingParam.parameterId,
  );
  const newActivityDefinition: ActivityParameter | undefined = newActivity.parameters?.find(
    (p) => p.id === existingParam.parameterId,
  );
  let checksPassed = true;

  if (existingParam.typeName !== newParam.typeName) {
    checksPassed = false;
  }

  // No smartness. If the previous param was a single value and the new one is multi-value, we still fall back to the default
  // without preserving the single value. We agreed to be aggressive, see SEL11-661
  if (existingParam.multiSelect !== newParam.multiSelect) {
    checksPassed = false;
  }

  if (existingParam.typeName === "TableParam") {
    checksPassed &&= isTableParamCompatible(oldActivityDefinition, newActivityDefinition);
  }

  // A StringParam can potentially switch from Select (controlled values) to a free text input when
  // the controlledValues case uses multiSelect: false. multiSelect does not exist on text params
  // without controlledValues.
  if (existingParam.typeName === "StringParam") {
    checksPassed &&= isStringParamCompatible(oldActivityDefinition, newActivityDefinition);
  }

  if (existingParam.typeName === "FileParam") {
    checksPassed &&= isFileParamCompatible(oldActivityDefinition, newActivityDefinition);
  }

  if (existingParam.typeName === "GenomeParam") {
    checksPassed &&= isGenomeParamCompatible(oldActivityDefinition, newActivityDefinition);
  }

  if (existingParam.typeName === "TrackerParam") {
    checksPassed &&= isTrackerParamCompatible(oldActivityDefinition, newActivityDefinition);
  }

  return checksPassed;
}

// ========== Private helpers ==========

function isGenomeParamCompatible(
  oldActivityDefinition: ActivityParameter | undefined,
  newActivityDefinition: ActivityParameter | undefined,
) {
  const existingGenomeParam = oldActivityDefinition as GenomeParam | undefined;
  const newGenomeParam = newActivityDefinition as GenomeParam | undefined;

  return (
    areArraysEqualIgnoreOrder(
      existingGenomeParam?.constraints?.allowedTypes ?? [],
      newGenomeParam?.constraints?.allowedTypes ?? [],
    ) &&
    areArraysEqualIgnoreOrder(
      existingGenomeParam?.constraints?.genomeSelectionType ?? [],
      newGenomeParam?.constraints?.genomeSelectionType ?? [],
    )
  );
}

function isFileParamCompatible(
  oldActivityDefinition: ActivityParameter | undefined,
  newActivityDefinition: ActivityParameter | undefined,
) {
  const existingFileParam = oldActivityDefinition as FileParam | undefined;
  const newFileParam = newActivityDefinition as FileParam | undefined;

  // Incompatible if selectable file types changed
  return (
    areArraysEqualIgnoreOrder(
      existingFileParam?.constraints?.fileTypes ?? [],
      newFileParam?.constraints?.fileTypes ?? [],
    ) && existingFileParam?.constraints?.fileSelectionType === newFileParam?.constraints?.fileSelectionType
  );
}

function isStringParamCompatible(
  oldActivityDefinition: ActivityParameter | undefined,
  newActivityDefinition: ActivityParameter | undefined,
) {
  const existingStringParam = oldActivityDefinition as StringParam | undefined;
  const newStringParam = newActivityDefinition as StringParam | undefined;

  return areArraysEqualIgnoreOrder(
    existingStringParam?.constraints?.controlledValues?.flatMap((v: StringControlledValue) => v.value) ?? [],
    newStringParam?.constraints?.controlledValues?.flatMap((v: StringControlledValue) => v.value) ?? [],
  );
}

function isTableParamCompatible(
  oldActivityDefinition: ActivityParameter | undefined,
  newActivityDefinition: ActivityParameter | undefined,
) {
  const oldType = oldActivityDefinition as TableParam | undefined;
  const newType = newActivityDefinition as TableParam | undefined;
  return oldType?.tableType === newType?.tableType;
}

function isTrackerParamCompatible(
  oldActivityDefinition: ActivityParameter | undefined,
  newActivityDefinition: ActivityParameter | undefined,
) {
  const oldType = oldActivityDefinition as TrackerParam | undefined;
  const newType = newActivityDefinition as TrackerParam | undefined;
  return oldType?.tracker === newType?.tracker;
}
